import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { TEAM_IMG, COLLAB_IMG, HERO_IMAGES } from "../mock";
import HERO_LIGHT_BG_IMG from "../assets/hero-light-bg.jpg";
import {
  UserPlus, Target, Wrench, BadgeCheck, Compass, GraduationCap, FlaskConical,
  ClipboardCheck, Star, Layers, Clock, Users, ArrowRight, CheckCircle2
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-[#15348C]/[0.06] border border-[#15348C]/10 px-4 py-1.5 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-[#8BD835]"/>
    {children}
  </span>
);

const MOSAIC_AREAS = [
  { icon: UserPlus, title: "Structured Onboarding", headline: "Start with the Right Foundation", desc: "Programs that introduce new hires to relevant technologies, processes, tools, and role expectations." },
  { icon: Target, title: "Role-Based Training", headline: "Learn What the Role Requires", desc: "Training aligned with specific job roles, technology stacks, and the practical knowledge required by your teams." },
  { icon: Wrench, title: "Hands-On Practice", headline: "Apply Learning in Practical Scenarios", desc: "Exercises, labs, projects, and simulations that help new hires apply their knowledge in relevant situations." },
  { icon: BadgeCheck, title: "Readiness Assessment", headline: "Measure Skills Before Deployment", desc: "Structured assessments and evaluations to understand knowledge, practical application, and role readiness." }
];

const MOSAIC_COLORS = {
  blue: { badge: "bg-[#2F6FF0]", ring: "border-[#2F6FF0]/30", text: "text-[#2F6FF0]", panel: "bg-[#EEF3FF] border-[#15348C]/10", hover: "sg-card--blue" },
  teal: { badge: "bg-[#0F9D8B]", ring: "border-[#0F9D8B]/30", text: "text-[#0F9D8B]", panel: "bg-[#EAFBF6] border-[#0F9D8B]/10", hover: "sg-card--teal" },
  orange: { badge: "bg-[#8BD835]", ring: "border-[#8BD835]/30", text: "text-[#4F9622]", panel: "bg-[#FFF3E6] border-[#8BD835]/15", hover: "sg-card--orange" }
};

const PROGRAM_STAGES = [
  { icon: Compass, title: "Foundation Learning", desc: "Introduce essential technologies, tools, processes, and concepts relevant to the organization and role." },
  { icon: GraduationCap, title: "Role-Specific Training", desc: "Learning paths designed around the responsibilities and skills required for specific job roles." },
  { icon: FlaskConical, title: "Practical Application", desc: "Hands-on labs, exercises, projects, and scenarios that connect learning with practical application." },
  { icon: ClipboardCheck, title: "Continuous Assessment", desc: "Regular evaluations help track learning progress and identify readiness for the next stage." }
];

const METRICS = [
  { icon: Star, value: "98%", label: "Client Satisfaction", desc: "Programs designed around specific organizational and learning requirements." },
  { icon: Layers, value: "500+", label: "Enterprise Batches Delivered", desc: "Experience delivering structured learning programs across technologies and teams." },
  { icon: Clock, value: "100,000+", label: "Learning Hours Delivered", desc: "Extensive instructor-led and practical learning experiences delivered across enterprise programs." },
  { icon: Users, value: "100+", label: "Enterprise Trainers", desc: "Access to experienced trainers across AI, cloud, data, cybersecurity, and other technology domains." }
];

const NewHireTrainingPage = () => {
  const [Mosaic0, Mosaic1, Mosaic2, Mosaic3] = MOSAIC_AREAS.map(a => a.icon);
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundImage: `url(${HERO_LIGHT_BG_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="relative max-w-7xl mx-auto px-4 py-14 lg:py-16 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-white border border-[#15348C]/10 px-4 py-2 rounded-full mb-7 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#6BBE32] sg-pulse"/>
              NEW HIRE TRAINING
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold leading-[1.12] text-[#0B1E4D]">Turn New Hires into Confident Contributors</h1>
            <p className="mt-5 text-gray-600 text-base lg:text-lg leading-relaxed max-w-xl">
              Structured training programs that help graduate cohorts and lateral hires develop the knowledge, practical skills, and role understanding required to contribute effectively from the start.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                Talk to Our Experts <ArrowRight size={16}/>
              </Link>
            </div>
          </Reveal>
          <Reveal variant="right" delay={100}>
            <div className="relative">
              <img
                src={TEAM_IMG}
                alt="New hires collaborating during onboarding training"
                className="w-full h-[340px] lg:h-[420px] object-cover rounded-2xl shadow-2xl ring-1 ring-black/5"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0A1F5C]/40 via-transparent to-transparent"/>
              <div className="absolute -bottom-7 -left-6 sm:left-0 bg-white rounded-xl shadow-xl p-4 pr-6 flex items-center gap-3.5 max-w-[240px]">
                <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/12 flex items-center justify-center">
                  <Star size={18} className="text-[#6BBE32]"/>
                </div>
                <div>
                  <div className="text-lg font-bold text-[#15348C] leading-none">98%</div>
                  <div className="text-[11px] text-gray-500 mt-1">Client Satisfaction</div>
                </div>
              </div>
              <div className="absolute -top-5 -right-4 bg-white rounded-xl shadow-xl px-4 py-3 flex items-center gap-2.5">
                <Users size={16} className="text-[#4F9622]"/>
                <div className="text-xs font-bold text-[#15348C]">100+ Enterprise Trainers</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mosaic — 4 training areas */}
      <section className="relative bg-[#F8F9FA] py-12 lg:py-14 overflow-hidden">
        <div
          className="absolute top-4 left-0 w-40 h-40 opacity-70 pointer-events-none hidden lg:block"
          style={{ backgroundImage: "radial-gradient(#CBD5E1 1.6px, transparent 1.6px)", backgroundSize: "16px 16px" }}
        />
        <div className="relative max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <Eyebrow>TRAINING APPROACH</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold leading-tight text-[#0B1E4D]">New Hire Training for Faster Role Readiness</h2>
              <p className="mt-3 text-gray-600">Structured programs designed around your technology environment, job roles, and the skills new hires need to perform effectively.</p>
              <span className="mt-3 inline-block w-10 h-0.5 rounded-full bg-[#2F6FF0]"/>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-5 gap-6 items-stretch">
            {/* 01 — Structured Onboarding (large feature card) */}
            <Reveal variant="up" className="lg:col-span-3">
              <div className="sg-card group relative rounded-2xl overflow-hidden h-full min-h-[300px] shadow-sm bg-[#0A1330] p-8 lg:p-10 flex flex-col">
                <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#2F6FF0]/20 rounded-full blur-3xl"/>
                <div className="relative flex items-center gap-3">
                  <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${MOSAIC_COLORS.blue.badge} text-white font-bold text-sm`}>01</span>
                  <span className="w-10 h-10 flex items-center justify-center rounded-full border border-white/40">
                    <Mosaic0 size={18} className="text-white"/>
                  </span>
                </div>
                <h3 className="relative mt-6 text-2xl lg:text-3xl font-bold text-white leading-tight">{MOSAIC_AREAS[0].title}</h3>
                <span className={`relative mt-4 w-10 h-0.5 block ${MOSAIC_COLORS.blue.badge}`}/>
                <div className="relative mt-4">
                  <div className="text-[#8BD835] text-sm font-semibold">{MOSAIC_AREAS[0].headline}</div>
                  <p className="mt-2 text-gray-300 text-sm leading-relaxed max-w-md">{MOSAIC_AREAS[0].desc}</p>
                </div>
              </div>
            </Reveal>

            {/* 02 & 03 — right-hand stacked cards */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <Reveal variant="up" delay={80}>
                <div className={`sg-card ${MOSAIC_COLORS.blue.hover} relative rounded-2xl overflow-hidden p-6 h-full min-h-[136px] border ${MOSAIC_COLORS.blue.panel}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${MOSAIC_COLORS.blue.badge} text-white font-bold text-sm`}>02</span>
                    <span className={`w-10 h-10 flex items-center justify-center rounded-full border bg-white ${MOSAIC_COLORS.blue.ring}`}>
                      <Mosaic1 size={18} className={MOSAIC_COLORS.blue.text}/>
                    </span>
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-[#15348C]">{MOSAIC_AREAS[1].title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{MOSAIC_AREAS[1].desc}</p>
                </div>
              </Reveal>
              <Reveal variant="up" delay={160}>
                <div className={`sg-card ${MOSAIC_COLORS.teal.hover} relative rounded-2xl overflow-hidden p-6 h-full min-h-[136px] border ${MOSAIC_COLORS.teal.panel}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${MOSAIC_COLORS.teal.badge} text-white font-bold text-sm`}>03</span>
                    <span className={`w-10 h-10 flex items-center justify-center rounded-full border bg-white ${MOSAIC_COLORS.teal.ring}`}>
                      <Mosaic2 size={18} className={MOSAIC_COLORS.teal.text}/>
                    </span>
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-[#15348C]">{MOSAIC_AREAS[2].title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{MOSAIC_AREAS[2].desc}</p>
                </div>
              </Reveal>
            </div>
          </div>

          {/* 04 — Readiness Assessment (banner) */}
          <Reveal variant="up" delay={240}>
            <div className={`sg-card ${MOSAIC_COLORS.orange.hover} mt-6 relative rounded-2xl overflow-hidden border ${MOSAIC_COLORS.orange.panel} shadow-lg shadow-[#8BD835]/10`}>
              <div className="flex flex-col lg:flex-row items-center">
                <div className="flex-1 w-full p-5 lg:pl-8 lg:pr-6 lg:py-6">
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-lg ${MOSAIC_COLORS.orange.badge} text-white font-bold text-sm`}>04</span>
                    <span className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full border bg-white ${MOSAIC_COLORS.orange.ring}`}>
                      <Mosaic3 size={18} className={MOSAIC_COLORS.orange.text}/>
                    </span>
                    <h3 className="text-xl lg:text-2xl font-bold text-[#15348C]">{MOSAIC_AREAS[3].title}</h3>
                  </div>
                  <div className={`mt-2.5 text-sm font-semibold ${MOSAIC_COLORS.orange.text}`}>{MOSAIC_AREAS[3].headline}</div>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed max-w-lg">{MOSAIC_AREAS[3].desc}</p>
                  <div className="mt-4">
                    <Link to="/contact" className={`sg-btn-glow inline-flex items-center gap-2 ${MOSAIC_COLORS.orange.badge} hover:opacity-90 text-white font-semibold px-5 py-2.5 rounded-lg transition-opacity text-sm`}>
                      Talk to Our Experts <ArrowRight size={16}/>
                    </Link>
                  </div>
                </div>
                <div className="w-full lg:w-[55%] shrink-0">
                  <img src={HERO_IMAGES[1]} alt={MOSAIC_AREAS[3].title} className="w-full h-40 lg:h-36 object-cover"/>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Program approach — image + 4 cards */}
      <section className="bg-white py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img src={COLLAB_IMG} alt="New hire training program journey" className="rounded-2xl shadow-xl ring-1 ring-black/5 w-full h-[440px] object-cover"/>
          </Reveal>
          <Reveal variant="right" delay={80}>
            <Eyebrow>PROGRAM APPROACH</Eyebrow>
            <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">A Structured Journey from Joining to Role Readiness</h2>
            <p className="mt-3 text-gray-600 leading-relaxed">New hires follow a focused learning journey that combines foundational knowledge, role-specific training, practical application, and assessment.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {PROGRAM_STAGES.map(s => (
                <div key={s.title} className="sg-card flex gap-3.5 bg-white border border-gray-200/80 rounded-xl p-5 shadow-sm">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/12 flex items-center justify-center">
                    <s.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div>
                    <div className="font-bold text-[#15348C] text-sm">{s.title}</div>
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Measurable impact */}
      <section className="bg-[#FFFAF0] py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <Eyebrow>MEASURABLE IMPACT</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Training That Goes Beyond Course Completion</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {METRICS.map((m, i) => (
              <Reveal key={m.label} variant="up" delay={i * 80}>
                <div className="sg-card group bg-white border border-gray-200/80 border-t-4 border-t-transparent hover:border-t-[#6BBE32] rounded-2xl p-7 h-full shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#6BBE32]/12 flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                    <m.icon size={22} className="text-[#6BBE32] group-hover:text-white transition-colors"/>
                  </div>
                  <div className="text-4xl font-bold text-[#15348C] tracking-tight">{m.value}</div>
                  <div className="text-sm font-semibold text-[#15348C] mt-2">{m.label}</div>
                  <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why SkillsGain */}
      <section className="bg-[#F8F9FA] py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img
              src={HERO_IMAGES[0]}
              alt="Structured new hire training delivered by SkillsGain"
              className="w-full h-[380px] object-cover rounded-2xl shadow-xl ring-1 ring-black/5"
            />
          </Reveal>
          <Reveal variant="right" delay={80}>
            <Eyebrow>WHY SKILLSGAIN</Eyebrow>
            <h2 className="mt-4 text-2xl lg:text-3xl font-bold text-[#15348C] leading-snug">Give New Hires a Strong Start in Their Roles</h2>
            <p className="mt-5 text-gray-600 leading-relaxed">
              Skillsgain works with organizations to design and deliver structured New Hire Training programs for graduate cohorts, lateral hires, and early-career professionals.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              Programs can be aligned with specific technologies, job roles, and organizational requirements, helping new hires develop relevant knowledge before taking on their responsibilities.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              Through instructor-led sessions, practical learning, hands-on exercises, and structured assessments, organizations can create a more consistent learning experience across new-hire batches.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] rounded-3xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#8BD835]/20 rounded-full blur-3xl"/>
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR HR, L&amp;D &amp; BUSINESS LEADERS</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Plan Your New Hire Training Program</h2>
                  <p className="mt-4 text-gray-300 leading-relaxed">Tell us about your upcoming hiring cohorts, target roles, and technology requirements. Our experts can help structure a training program aligned with your organizational needs.</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-gray-300">
                    {["Role-specific learning paths", "Technology-focused training", "Hands-on practice and projects", "Structured assessments and readiness evaluation"].map(t => (
                      <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#8BD835]"/>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                    Talk to Our Experts <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="border-2 border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request a Program Proposal</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default NewHireTrainingPage;
